import clsx from "clsx";
import {
  BookOpen,
  BriefcaseBusiness,
  Compass,
  Download,
  GalleryVerticalEnd,
  House,
  Lightbulb,
  MessageSquareText,
  Route,
  Settings,
} from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  TITLE_BAR_HEIGHT,
  WINDOW_CONTROLS_INSET,
  hasCustomTitleBar,
  isMacDesktopShell,
} from "../../utils/desktopShell";

// The sidebar's two widths, in px. App.tsx derives the content padding and the
// --layout-sidebar-width variable (which positions the top bar) from these, so
// they must stay the single source of truth. Applied as an inline style rather
// than a w-[...] class: Tailwind's scanner only sees literal class strings.
export const SIDEBAR_COLLAPSED_WIDTH = 68;
export const SIDEBAR_EXPANDED_WIDTH = 224;

// At low page zoom, leave room for the native cluster plus WindowControls'
// 102px button group and 10px of breathing room. Both the sidebar and content
// offset use this width so the toolbar cannot cover the history buttons.
export const getDashboardSidebarWidth = (collapsed: boolean) =>
  collapsed
    ? `${SIDEBAR_COLLAPSED_WIDTH}px`
    : isMacDesktopShell
      ? `max(${SIDEBAR_EXPANDED_WIDTH}px, calc(${WINDOW_CONTROLS_INSET} + 112px))`
      : `${SIDEBAR_EXPANDED_WIDTH}px`;

type DashboardSidebarProps = {
  collapsed: boolean;
  onNavigateOverview: () => void;
  overviewActive: boolean;
  onNavigateProcesses: () => void;
  processesActive: boolean;
  onNavigateRecordings: () => void;
  recordingsActive: boolean;
  activityLabel?: string;
  onNavigateSettings: () => void;
  settingsActive: boolean;
  showDownloads: boolean;
  onNavigateDownloads: () => void;
  downloadsActive: boolean;
  showSectionDemos: boolean;
  onNavigateSectionDemos: () => void;
  sectionDemosActive: boolean;
  onNavigateVocabulary: () => void;
  vocabularyActive: boolean;
  showExecutiveDashboard: boolean;
  onNavigateExecutiveDashboard: () => void;
  executiveDashboardActive: boolean;
  onNavigateWatchlist: () => void;
  watchlistActive: boolean;
  /**
   * Tracks unread results themselves, not whether the tab has been visited.
   * It decrements as alerts are opened and disappears when the last is read.
   */
  watchlistUnreadCount: number;
  /** Window controls, rendered in the top strip while the sidebar owns it. */
  titleBarControls: React.ReactNode;
  /** Omitted where no feedback service is configured. */
  onShareFeedback?: () => void;
};

// Module scope, not inside the sidebar's render: an inline component gets a new
// identity every render, which remounts every button's DOM (killing hover state
// and transitions mid-animation). Collapsed to a rail there is no room for
// labels, so every row shows its icon alone and moves its label into a tooltip.
const SidebarButton = ({
  icon,
  label,
  active,
  collapsed,
  onClick,
  trailing,
  dataTour,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  collapsed: boolean;
  onClick: () => void;
  /** Sits at the row's right edge, past the label — so it needs the label to be there. */
  trailing?: React.ReactNode;
  /** Anchor for the demo tour's cards (data-tour attribute). */
  dataTour?: string;
}) => (
  <button
    type="button"
    data-tour={dataTour}
    onClick={onClick}
    className={clsx(
      "flex w-full items-center gap-2 rounded-lg py-2 text-sm transition",
      collapsed ? "px-0 justify-center" : "px-3 justify-start",
      active
        ? "bg-muted-surface text-text"
        : "text-text-dim hover:bg-muted-surface hover:text-text",
    )}
    title={collapsed ? label : undefined}
  >
    {icon}
    <span className={collapsed ? "hidden" : "inline"}>{label}</span>
    {!collapsed && trailing && <span className="ml-auto flex items-center">{trailing}</span>}
  </button>
);

const DashboardSidebar = ({
  collapsed,
  onNavigateOverview,
  overviewActive,
  onNavigateProcesses,
  processesActive,
  onNavigateRecordings,
  recordingsActive,
  activityLabel,
  onNavigateSettings,
  settingsActive,
  showDownloads,
  onNavigateDownloads,
  downloadsActive,
  showSectionDemos,
  onNavigateSectionDemos,
  sectionDemosActive,
  onNavigateVocabulary,
  vocabularyActive,
  showExecutiveDashboard,
  onNavigateExecutiveDashboard,
  executiveDashboardActive,
  onNavigateWatchlist,
  watchlistActive,
  watchlistUnreadCount,
  titleBarControls,
  onShareFeedback,
}: DashboardSidebarProps) => {
  const { t } = useTranslation();

  // Expanded, the sidebar is wide enough to host the title bar, so it runs the
  // full height and reserves a band at the top for the window controls (and,
  // in the desktop shell, the traffic lights). Collapsed, the rail is narrower
  // than those controls, so DashboardTopBar takes the whole top row instead
  // and the sidebar starts below it.
  const hostsTitleBar = !collapsed;

  return (
    <aside
      className={clsx(
        "fixed bottom-0 left-0 z-30 flex flex-col border-r border-outline bg-background-alt px-2 pb-5",
        !hostsTitleBar && "pt-3",
      )}
      style={{
        top: hostsTitleBar ? 0 : TITLE_BAR_HEIGHT,
        width: getDashboardSidebarWidth(collapsed),
      }}
    >
      {hostsTitleBar && (
        <div
          className={clsx("-mx-2 mb-2 flex shrink-0 items-center", hasCustomTitleBar && "app-drag")}
          style={{
            height: TITLE_BAR_HEIGHT,
            paddingLeft: WINDOW_CONTROLS_INSET,
          }}
        >
          {titleBarControls}
        </div>
      )}
      <nav className="flex-1 space-y-1">
        <SidebarButton
          collapsed={collapsed}
          icon={<House size={18} />}
          label={t("dashboard.overview")}
          active={overviewActive}
          onClick={onNavigateOverview}
        />
        <SidebarButton
          collapsed={collapsed}
          icon={<Route size={18} />}
          label={t("processes")}
          active={processesActive}
          onClick={onNavigateProcesses}
        />
        <SidebarButton
          collapsed={collapsed}
          icon={<GalleryVerticalEnd size={18} />}
          label={activityLabel ?? t("activity.team_title")}
          active={recordingsActive}
          onClick={onNavigateRecordings}
        />
        <SidebarButton
          collapsed={collapsed}
          icon={
            <span className="relative flex shrink-0">
              <Lightbulb size={18} />
              {/* With no label to sit beside, the count becomes a compact badge
                  on the icon; expanded, the orange dot and count move to the edge. */}
              {collapsed && watchlistUnreadCount > 0 && (
                <span className="absolute -right-2.5 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-unread px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-background-alt">
                  {watchlistUnreadCount > 99 ? "99+" : watchlistUnreadCount}
                </span>
              )}
            </span>
          }
          trailing={
            watchlistUnreadCount > 0 && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-unread">
                <span className="unread-pulse h-2 w-2 rounded-full bg-unread text-unread" />
                {watchlistUnreadCount}
              </span>
            )
          }
          label={t("watchlist.title")}
          active={watchlistActive}
          onClick={onNavigateWatchlist}
        />
      </nav>

      <div className="space-y-1 border-outline pt-3">
        {showSectionDemos && (
          <SidebarButton
            collapsed={collapsed}
            icon={<Compass size={18} />}
            label={t("tour.settings_title")}
            active={sectionDemosActive}
            onClick={onNavigateSectionDemos}
            dataTour="demo-gallery"
          />
        )}
        {showDownloads && (
          <SidebarButton
            collapsed={collapsed}
            icon={<Download size={18} />}
            label={t("downloads_page")}
            active={downloadsActive}
            onClick={onNavigateDownloads}
          />
        )}
        <SidebarButton
          collapsed={collapsed}
          icon={<BookOpen size={18} />}
          label={t("vocabulary")}
          active={vocabularyActive}
          onClick={onNavigateVocabulary}
        />
        {showExecutiveDashboard && (
          <SidebarButton
            collapsed={collapsed}
            icon={<BriefcaseBusiness size={18} />}
            label={t("executive_dashboard")}
            active={executiveDashboardActive}
            onClick={onNavigateExecutiveDashboard}
          />
        )}
      </div>
      <div className="space-y-1 border-t border-outline pt-3">
        {onShareFeedback && (
          <SidebarButton
            collapsed={collapsed}
            icon={<MessageSquareText size={18} />}
            label={t("feedback.title")}
            onClick={onShareFeedback}
          />
        )}
        <SidebarButton
          collapsed={collapsed}
          icon={<Settings size={18} />}
          label={t("Settings")}
          active={settingsActive}
          onClick={onNavigateSettings}
        />
      </div>
    </aside>
  );
};

export default DashboardSidebar;
